import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/layout/Navbar';
import Header from '@/components/layout/Header';
import { Trash2, Copy, MoreVertical, UploadCloud, FileIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from '@/components/ui/use-toast';
import { formatBytes } from '@/lib/utils';
import { fileService, FileInfo } from '@/services/api';
import { Card } from '@/components/ui/card';
import { useAuthRedirect } from '@/hooks/useAuthRedirect';

// Constants
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const FileManagement: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [files, setFiles] = useState<FileInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Use the auth redirect hook
  useAuthRedirect(false);

  // Effect to handle admin check and data fetching
  useEffect(() => {
    if (isAuthenticated && user && user.role !== 'admin') {
      // User is authenticated but not an admin
      navigate('/dashboard'); // Redirect non-admins
      return;
    }

    if (isAuthenticated && user && user.role === 'admin') {
       fetchFiles(); // Fetch only if authenticated admin
    } else if (!isAuthenticated) {
      // Hook handles redirect, set loading false
       setIsLoading(false);
    }
  }, [isAuthenticated, user, navigate]); // Depend on auth state and navigate

  const fetchFiles = async () => {
    setIsLoading(true);
    try {
      const filesList = await fileService.listFiles();
      setFiles(filesList);
    } catch (error) {
      console.error('Error loading files:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load files',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    setIsUploading(true);
    
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Check file size
        if (file.size > MAX_FILE_SIZE) {
          toast({
            variant: 'destructive',
            title: 'File too large',
            description: `${file.name} exceeds the maximum size of ${formatBytes(MAX_FILE_SIZE)}`,
          });
          continue;
        }
        
        await fileService.uploadFile(file);
        toast({
          title: 'File uploaded',
          description: `${file.name} has been uploaded successfully`,
        });
      }
      
      // Refresh the file list
      fetchFiles();
    } catch (error) {
      console.error('Error uploading files:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to upload one or more files',
      });
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleDelete = async (fileName: string) => {
    if (!confirm('Are you sure you want to delete this file?')) {
      return;
    }
    
    try {
      await fileService.deleteFile(fileName);
      setFiles(files.filter(f => f.fileName !== fileName));
      toast({
        title: 'File deleted',
        description: 'File has been deleted successfully',
      });
    } catch (error) {
      console.error('Error deleting file:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to delete file',
      });
    }
  };

  const copyToClipboard = (file: FileInfo) => {
    const markdownLink = fileService.getMarkdownLink(file);
    navigator.clipboard.writeText(markdownLink);
    toast({
      title: 'Copied to clipboard',
      description: 'Markdown link has been copied to clipboard',
    });
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen flex-col">
        <Navbar />
        
        <main className="flex-1 container py-6">
          <Header 
            title="File Management" 
            description="Upload and manage files"
          />
          
          {/* Upload Section */}
          <Card className="mb-6">
            <div 
              onClick={triggerFileInput}
              className="p-8 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/20 transition-colors"
            >
              <input 
                type="file" 
                ref={fileInputRef} 
                onChange={handleFileChange} 
                className="hidden" 
                multiple 
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip"
              />
              <div className="flex flex-col items-center justify-center gap-2 text-center">
                <UploadCloud className="h-12 w-12 text-muted-foreground" />
                <h3 className="text-lg font-semibold">
                  Click to upload files
                </h3>
                <p className="text-sm text-muted-foreground max-w-xs">
                  Maximum file size: {formatBytes(MAX_FILE_SIZE)}
                </p>
                {isUploading && (
                  <div className="mt-2">
                    <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-current border-r-transparent"></div>
                    <span className="ml-2">Uploading...</span>
                  </div>
                )}
              </div>
            </div>
          </Card>
          
          {/* Files Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]"></TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Upload Date</TableHead>
                  <TableHead className="w-[100px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center">
                    Loading files...
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      
      <main className="flex-1 container py-6">
        <Header 
          title="File Management" 
          description="Upload and manage files"
        />
        
        {/* Upload Section */}
        <Card className="mb-6">
          <div 
            onClick={triggerFileInput}
            className="p-8 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/20 transition-colors"
          >
            <input 
              type="file" 
              ref={fileInputRef} 
              onChange={handleFileChange} 
              className="hidden" 
              multiple 
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip"
            />
            <div className="flex flex-col items-center justify-center gap-2 text-center">
              <UploadCloud className="h-12 w-12 text-muted-foreground" />
              <h3 className="text-lg font-semibold">
                Click to upload files
              </h3>
              <p className="text-sm text-muted-foreground max-w-xs">
                Maximum file size: {formatBytes(MAX_FILE_SIZE)}
              </p>
              {isUploading && (
                <div className="mt-2">
                  <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-current border-r-transparent"></div>
                  <span className="ml-2">Uploading...</span>
                </div>
              )}
            </div>
          </div>
        </Card>
        
        {/* Files Table */}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]"></TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Size</TableHead>
                <TableHead>Upload Date</TableHead>
                <TableHead className="w-[100px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {files.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center">
                    No files uploaded yet
                  </TableCell>
                </TableRow>
              ) : (
                files.map((file) => (
                  <TableRow key={file.fileName}>
                    <TableCell>
                      <div className="h-10 w-10 flex items-center justify-center bg-muted rounded">
                        <FileIcon className="h-5 w-5 text-muted-foreground" />
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">
                      <a 
                        href={file.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {file.originalName}
                      </a>
                    </TableCell>
                    <TableCell>{file.contentType}</TableCell>
                    <TableCell>{formatBytes(file.size)}</TableCell>
                    <TableCell>{formatDate(file.uploadDate)}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                            <span className="sr-only">Open menu</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => copyToClipboard(file)}>
                            <Copy className="mr-2 h-4 w-4" />
                            Copy link
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(file.fileName)}>
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </main>
    </div>
  );
};

export default FileManagement; 